import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Activity, Pill, FlaskConical, Stethoscope, ClipboardList, FileCheck } from 'lucide-react';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';

function Section({ title, icon, count, children }: { title: string; icon: React.ReactNode; count: number; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-50">
        <div className="text-primary-500">{icon}</div>
        <h3 className="font-semibold text-text-primary">{title}</h3>
        <span className="ml-auto bg-gray-100 text-text-secondary text-xs font-medium px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <div className="divide-y divide-gray-50">
        {count === 0 ? (
          <p className="text-center py-6 text-text-secondary text-sm">Sin registros</p>
        ) : children}
      </div>
    </div>
  );
}

export function ExpedientePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: exp, isLoading } = useQuery({
    queryKey: ['expediente', id],
    queryFn: () => api.get(`/api/pacientes/${id}/expediente`).then(r => r.data),
  });

  const { data: paciente } = useQuery({
    queryKey: ['paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}`).then(r => r.data),
  });

  if (isLoading) return <div className="text-center py-16 text-text-secondary">Cargando expediente...</div>;

  const nombre = paciente ? `${paciente.nombre} ${paciente.apellidoPaterno}` : 'Paciente';

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(`/pacientes/${id}`)}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft size={16} /> Volver a la ficha de {nombre}
      </button>

      <div>
        <h2 className="text-2xl font-bold text-text-primary">Expediente Clínico</h2>
        <p className="text-sm text-text-secondary mt-0.5">{nombre}</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {[
          { label: 'Exploraciones', val: exp?.exploraciones?.length || 0 },
          { label: 'Diagnósticos', val: exp?.diagnosticos?.length || 0 },
          { label: 'Recetas', val: exp?.recetas?.length || 0 },
          { label: 'Análisis', val: exp?.analisis?.length || 0 },
          { label: 'Laboratorios', val: exp?.laboratorios?.length || 0 },
          { label: 'Consentimientos', val: exp?.consentimientos?.length || 0 },
        ].map(item => (
          <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-primary-600">{item.val}</p>
            <p className="text-xs text-text-secondary mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      <Section title="Diagnósticos" icon={<Activity size={18} />} count={exp?.diagnosticos?.length || 0}>
        {exp?.diagnosticos?.map((d: any) => (
          <div key={d.id} className="px-5 py-3 hover:bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{d.descripcion || d.cie10 || 'Sin descripción'}</p>
                {d.cie10 && d.descripcion && <p className="text-xs text-text-secondary mt-0.5">CIE-10: {d.cie10}</p>}
              </div>
              <span className="text-xs text-text-secondary">{d.createdAt ? formatDate(d.createdAt) : ''}</span>
            </div>
          </div>
        ))}
      </Section>

      <Section title="Exploraciones físicas" icon={<Stethoscope size={18} />} count={exp?.exploraciones?.length || 0}>
        {exp?.exploraciones?.map((e: any) => (
          <div key={e.id} className="px-5 py-3 hover:bg-gray-50/50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-4 text-sm flex-wrap">
                {e.peso && <span className="text-text-secondary">Peso: <span className="text-text-primary font-medium">{e.peso} kg</span></span>}
                {e.talla && <span className="text-text-secondary">Talla: <span className="text-text-primary font-medium">{e.talla} cm</span></span>}
                {e.presionArterial && <span className="text-text-secondary">PA: <span className="text-text-primary font-medium">{e.presionArterial}</span></span>}
                {e.frecuenciaCardiaca && <span className="text-text-secondary">FC: <span className="text-text-primary font-medium">{e.frecuenciaCardiaca} lpm</span></span>}
                {e.temperatura && <span className="text-text-secondary">Temp: <span className="text-text-primary font-medium">{e.temperatura} °C</span></span>}
              </div>
              <span className="text-xs text-text-secondary flex-shrink-0">{e.createdAt ? formatDate(e.createdAt) : ''}</span>
            </div>
            {e.notasMedico && <p className="text-xs text-text-secondary mt-1">{e.notasMedico}</p>}
          </div>
        ))}
      </Section>

      <Section title="Recetas" icon={<Pill size={18} />} count={exp?.recetas?.length || 0}>
        {exp?.recetas?.map((r: any) => (
          <div key={r.id} className="px-5 py-3 hover:bg-gray-50/50">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-text-primary">Folio: {r.folio}</p>
              <span className="text-xs text-text-secondary">{r.fecha ? formatDate(r.fecha) : ''}</span>
            </div>
            {r.indicaciones && <p className="text-xs text-text-secondary">{r.indicaciones}</p>}
            {r.medicaciones?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.medicaciones.map((m: any, i: number) => (
                  <span key={i} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{m.denominacionGenerica} {m.dosis}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Análisis" icon={<FlaskConical size={18} />} count={exp?.analisis?.length || 0}>
          {exp?.analisis?.map((a: any) => (
            <div key={a.id} className="px-5 py-3 hover:bg-gray-50/50">
              <p className="text-sm font-medium text-text-primary">{a.tipo || 'Análisis'}</p>
              <span className="text-xs text-text-secondary">{a.fecha ? formatDate(a.fecha) : ''}</span>
            </div>
          ))}
        </Section>

        <Section title="Laboratorios" icon={<ClipboardList size={18} />} count={exp?.laboratorios?.length || 0}>
          {exp?.laboratorios?.map((l: any) => (
            <div key={l.id} className="px-5 py-3 hover:bg-gray-50/50">
              <p className="text-sm font-medium text-text-primary">{l.nombre || 'Laboratorio'}</p>
              <span className="text-xs text-text-secondary">{l.createdAt ? formatDate(l.createdAt) : ''}</span>
            </div>
          ))}
        </Section>
      </div>

      <Section title="Consentimientos" icon={<FileCheck size={18} />} count={exp?.consentimientos?.length || 0}>
        {exp?.consentimientos?.map((c: any) => (
          <div key={c.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50/50">
            <div>
              <p className="text-sm font-medium text-text-primary capitalize">{c.tipo?.replace('_', ' ') || 'Consentimiento'}</p>
              <span className="text-xs text-text-secondary">{c.createdAt ? formatDate(c.createdAt) : ''}</span>
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              c.estado === 'firmado' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {c.estado === 'firmado' ? 'Firmado' : 'Pendiente'}
            </span>
          </div>
        ))}
      </Section>
    </div>
  );
}
